import React from "react";
import _ from "lodash";
import { DateTime, Interval } from "luxon";
import { inject, observer } from "mobx-react";
import { reaction } from "mobx";
import bracket from "../../../../shared/bracket";
import {
  detectedType,
  formatTime,
  TimeScale,
} from "../../../../shared/dateTimeUtils";
import Widget from "../../../Widget";
import AnnotationPopover from "./AnnotationPopover";
import "./ObWaveform.scss";
import ObWaveformStore from "./ObWaveformStore";
import annotationIcon from "./summary_ob_annotations@2x.png";
import multiAnnotationIcon from "./summary_ob_multi_annotations@2x.png";
import { minuteFormat } from "../../ObSummaryStore";
import TimeAgo from "../../../../shared/TimeAgo";

const hrMin = 50;
const hrMax = 210;
const uaMin = 0;
const uaMax = 100;

class ObWaveform extends Widget {
  canvases = {};
  gridLineWidth = 1;
  chartLineWidth = 1.5;
  gridLinePrimaryAlpha = 0.8;
  gridLineSecondaryAlpha = 0.2;

  labels = {
    Baby1HR: "Fetal HR 1",
    Baby2HR: "Fetal HR 2",
    Baby3HR: "Fetal HR 3",
    MotherHR: "Mother HR",
    MotherUA: "Contractions",
  };

  labelOrder = ["Baby1HR", "Baby2HR", "Baby3HR", "MotherHR", "MotherUA"];

  set startTime(startTime) {
    this._setStartTime(startTime);
  }
  _setStartTime = this.frameDebounce((startTime) => {
    this.props.summaryStore.startTime = startTime;
  });

  get startTime() {
    return this.props.summaryStore.startTime;
  }

  set endTime(endTime) {
    this._setEndTime(endTime);
  }
  _setEndTime = this.frameDebounce((endTime) => {
    this.props.summaryStore.endTime = endTime;
  });

  get endTime() {
    return this.props.summaryStore.endTime;
  }

  constructor(props) {
    super(props);
    this.store = new ObWaveformStore(this.props);
    props.summaryStore.preventZoom = props.preventZoom;
    this.disposeAnnotations = reaction(
      () => props.summaryStore.tiles.annotations,
      () => this.setAnnotations(),
    );
    this.setChartSizes = this.frameDebounce(this.setChartSizes.bind(this));
  }

  componentDidMount() {
    this.setAnnotations();
    this.getColors();
    this.setChartSizes();
    this.resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(this.setChartSizes),
    );
    if (this.div) this.resizeObserver.observe(this.div);
    if (window.Modernizr.passiveeventlisteners) {
      this.div.addEventListener("wheel", this.onWheel, {
        passive: false,
        capture: true,
      });
    } else {
      this.div.addEventListener("wheel", this.onWheel, true);
    }
  }

  componentDidUpdate(prevProps) {
    this.renderCharts();
    if (this.props.selectedPatient !== prevProps.selectedPatient) {
      requestAnimationFrame(() => {
        this.setChartSizes();
      });
    }
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
    this.disposeAnnotations();
  }

  get waveKeys() {
    return Array.from(
      this.props.model.records.reduce((keySet, record) => {
        record.waves.forEach((w) => keySet.add(w.type));
        return keySet;
      }, new Set()),
    );
  }

  get hasData() {
    return _.get(this.props, "model.records", []).length > 0;
  }

  render() {
    const chartRange = Interval.fromDateTimes(this.startTime, this.endTime);

    return (
      <div
        className={this.cn({
          "ob-waveform component": 1,
          "live-mode-loading": this.props.summaryStore.liveModeLoading,
        })}
        onScroll={(e) => e.preventDefault()}
        onMouseDown={this.onMouseDown}
        ref={(el) => (this.div = el)}
      >
        {!this.hasData && (
          <div className="no-data-available">No Data Available</div>
        )}
        <TimeScale startTime={this.startTime} endTime={this.endTime} />
        <div className="HR">
          <canvas
            ref={(ref) => {
              this.canvases.hr = this.canvases.hr || ref;
            }}
          />
        </div>

        {!this.props.preventZoom &&
        this.props.summaryStore.selectedAnnotation ? (
          // only show annotation when we're not in MPV
          <AnnotationPopover
            selectedAnnotation={this.props.summaryStore.selectedAnnotation}
            timeToPosition={this.timeToPosition}
            width={this.canvases.hr?.width}
            annotations={this.store.annotations}
          />
        ) : null}
        <div className="annotations">
          {this.store.annotations
            .filter((annotation) => chartRange.contains(annotation.dateTime))
            .map((annotation) => (
              <div
                key={annotation.annotationId}
                className="annotation"
                style={{
                  left: `${
                    this.timeToPosition({
                      time: annotation.recordTime,
                      relative: true,
                    }) * 100
                  }%`,
                }}
                title={`${formatTime(
                  DateTime.fromISO(annotation.recordTime),
                )} - ${annotation.annotation}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // only show annotation when we're not in MPV
                  if (!this.props.preventZoom) {
                    this.selectAnnotation(annotation);
                  }
                }}
              >
                <img
                  src={
                    annotation.isOverlapping
                      ? multiAnnotationIcon
                      : annotationIcon
                  }
                  alt="annotation"
                />
              </div>
            ))}
        </div>
        <div className="UA">
          <canvas ref={(ref) => (this.canvases.ua = this.canvases.ua || ref)} />
        </div>

        <footer>
          <TimeAgo endTime={this.endTime} />
          <div className="ob-legend">
            {this.hasData &&
              _.uniq(
                this.props.model.records.flatMap((r) =>
                  r.waves.map((w) => w.type),
                ),
              )
                .sort(
                  (a, b) =>
                    this.labelOrder.indexOf(a) - this.labelOrder.indexOf(b),
                )
                .map((key) => (
                  <div key={key} className="key">
                    <label>{this.labels[key]}</label>
                    <div
                      className="sample"
                      style={{ borderBottomColor: this.colorMap[key] }}
                    ></div>
                  </div>
                ))}
          </div>
        </footer>

        <div className="colors" ref={(el) => (this.colorsRef = el)}>
          <div className="brown" />
          <div className="blue" />
          <div className="violet" />
        </div>
      </div>
    );
  }

  selectAnnotation(_annotation) {
    let annotation = _annotation;
    if (annotation.isOverlapping) {
      const minute = annotation.dateTime.toFormat(minuteFormat);
      annotation = this.store.annotations.find(
        (a) => a.dateTime.toFormat(minuteFormat) === minute,
      );
    }
    this.props.summaryStore.selectAnnotation(annotation, true);
  }
  setAnnotations() {
    this.store.annotations = (
      this.props.summaryStore?.tiles.annotations?.model?.annotationGroups || []
    ).reduce((acc, group) => [...acc, ...group.annotations], []);
  }

  fromMillisToLuxon(millis) {
    if (detectedType(millis) === "number") {
      return DateTime.fromMillis(millis);
    } else if (detectedType(millis) === "luxon") {
      return millis;
    }
  }

  onWheel = (e) => {
    if (!this.shouldZoom) return;
    e.preventDefault();
    this.props.summaryStore.liveMode = false;
    const rect = this.div.getClientRects()[0];
    const relativeX = (e.clientX - rect.x) / rect.width;
    this.startTime = this.fromMillisToLuxon(
      this.zoomStartTime({ e, relativeX }),
    );
    this.endTime = this.fromMillisToLuxon(this.zoomEndTime({ e, relativeX }));
  };

  zoomStartTime = zoomStartTime;
  zoomEndTime = zoomEndTime;

  get shouldZoom() {
    return (
      !this.props.preventZoom &&
      !this.renderingCharts &&
      this.div?.getClientRects
    );
  }

  onMouseDown = (e) => {
    if (this.props.preventZoom) return;
    // Start drag.
    this.props.summaryStore.liveMode = false;
    this.mouseDownX = e.clientX;
    this.mouseDownStartTime = this.startTime;
    this.mouseDownEndTime = this.endTime;
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("selectstart", this.onSelectStart);
  };

  onMouseMove = (e) => {
    if (!this.mouseDownX) return;
    // Drag.

    const deltaX = this.mouseDownX - e.clientX;
    const deltaMs = deltaX / this.pixelsPerMs;
    const newStartTime = this.mouseDownStartTime?.plus({
      milliseconds: deltaMs,
    });
    const newEndTime = this.mouseDownEndTime?.plus({ milliseconds: deltaMs });
    // if they're trying to drag before the start of the data, stop.
    if (newStartTime < this.props.summaryStore.minTime) {
      this.props.summaryStore.liveMode = false;
      return;
    }
    // if they're trying to drag into the future, goto live mode.
    const now = DateTime.local();
    if (newEndTime >= now) {
      this.props.summaryStore.liveMode = true;
      return;
    }
    this.props.summaryStore.liveMode = false;
    this.startTime = newStartTime;
    this.endTime = newEndTime;
  };
  onMouseUp = () => {
    // End drag.
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("selectstart", this.onSelectStart);
  };
  onSelectStart(e) {
    // Prevent selecting text as a side-effect of dragging the chart.
    e.preventDefault();
  }
  get colorMap() {
    if (!this.colors) return {};
    return {
      Baby1HR: "#000",
      Baby2HR: this.colors.brown,
      Baby3HR: this.colors.blue,
      MotherHR: this.colors.violet,
      MotherUA: this.colors.dark_gray,
    };
  }

  renderCharts() {
    if (!this.startTime) return;
    if (!this.props.model?.records) return;
    this.rendering = true;

    this.clearCanvases();
    this.renderGrids();

    // render data
    const pixelsPerMillisecond = this.pixelsPerMs;

    const start = this.startTime.minus({ minutes: 1 });
    const end = this.endTime.plus({ minutes: 1 });
    const startTimeInMillis = this.startTime.toMillis();
    this.props.model.records
      .filter((record) => {
        return +record.startTime > +start && +record.endTime < +end;
      })
      .forEach((record) => {
        // each record is one minute.

        const recordStartTimeInMillis = record.startTime.toMillis();
        const recordOffsetXCoordinate =
          (recordStartTimeInMillis - startTimeInMillis) * pixelsPerMillisecond;

        record.waves.forEach((wave) => {
          const isHeartRateWaveType = wave.type.match(/HR$/);
          const canvas = isHeartRateWaveType
            ? this.canvases.hr
            : this.canvases.ua;
          const canvas2dContext = canvas.getContext("2d");
          canvas2dContext.lineWidth = 2;
          canvas2dContext.lineWidth = this.chartLineWidth;
          canvas2dContext.strokeStyle = this.colorMap[wave.type] || "#000";
          let isNewLine = true;
          canvas2dContext.beginPath();

          const samplesPerMillisecond = wave.samplesPerSecond / 1000;
          const pixelsPerSample = pixelsPerMillisecond / samplesPerMillisecond;
          wave.points.forEach((point, i) => {
            const xCoordinate = recordOffsetXCoordinate + pixelsPerSample * i;
            if (xCoordinate < -pixelsPerSample * 2) return;
            if (xCoordinate > canvas.width + pixelsPerSample * 2) return;

            if (point.value === 0) {
              // null value, break the line
              if (!isNewLine) {
                canvas2dContext.stroke();
                canvas2dContext.beginPath();
                isNewLine = true;
              }
              return;
            }

            if (typeof point.y === "undefined") {
              point.y = isHeartRateWaveType
                ? this.hrToPosition(point.value)
                : this.mmHgToPosition(point.value);
            }

            if (isNewLine) {
              // start the line
              canvas2dContext.moveTo(xCoordinate, point.y);
              isNewLine = false;
            } else {
              canvas2dContext.lineTo(xCoordinate, point.y);
            }
          });
          canvas2dContext.stroke();
        });
      });
    this.rendering = false;
  }

  setChartSizes = () => {
    if (!this.canvases) return;
    let resized;
    _.each(this.canvases, (canvas) => {
      if (!canvas) return;
      canvas.getCli;
      const targetWidth = canvas.clientWidth * devicePixelRatio;
      const targetHeight = canvas.clientHeight * devicePixelRatio;
      if (!targetHeight || !targetWidth) return;
      if (canvas.width !== targetWidth) {
        canvas.width = targetWidth;
        this.store.width = targetWidth;
        resized = true;
      }
      if (
        (targetHeight && canvas.height < targetHeight * 0.98) ||
        canvas.height > targetHeight * 1.02
      ) {
        canvas.height = targetHeight;
        this.invalidateCachedYs();
        resized = true;
      }
    });
    if (resized) {
      this.renderCharts();
    }
  };

  invalidateCachedYs() {
    this.props.model.records?.forEach((record) => {
      record.waves.forEach((wave) =>
        wave.points.forEach((point) => (point.y = undefined)),
      );
    });
  }

  clearCanvases() {
    _.each(this.canvases, (canvas) => {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    });
  }

  timeToPosition = ({ time, pixelsPerMs = this.pixelsPerMs, relative }) => {
    let msFromLeft;
    if (detectedType(time) === "luxon") {
      msFromLeft = time.diff(this.startTime).as("milliseconds");
    } else if (detectedType(time) === "string") {
      msFromLeft = DateTime.fromISO(time)
        .diff(this.startTime)
        .as("milliseconds");
    }
    if (relative) return msFromLeft / this.chartLengthInMilliseconds;
    return msFromLeft * pixelsPerMs ?? 0;
  };

  get chartLengthInMilliseconds() {
    return this.endTime?.diff(this.startTime).as("milliseconds") ?? 0;
  }

  get pixelsPerMs() {
    return (
      (this.canvases.hr?.width ?? window.innerWidth) /
      this.chartLengthInMilliseconds
    );
  }

  get pixelsPerBpm() {
    return calcPixelsPerBpm(this.canvases.hr.height);
  }

  hrToPosition = hrToPosition;

  mmHgToPosition(mmHg) {
    // Convert Millimeters of Mercury value to a Y-coordinate for contractions chart.
    const min = uaMin;
    const max = uaMax;
    const mmHgRange = max - min;
    const pixelsPermmHg = this.canvases.ua.height / mmHgRange;
    return this.canvases.ua.height - (mmHg - min) * pixelsPermmHg;
  }

  renderGrids() {
    // render grids.
    // draw horizontal lines.
    for (let i = hrMin; i <= hrMax; i += 10) {
      const ctx = this.canvases.hr.getContext("2d");
      this.drawHorizontalLine({
        ctx,
        value: i,
        alpha: i % 30 ? this.gridLineSecondaryAlpha : this.gridLinePrimaryAlpha,
      });
    }
    for (let i = uaMin; i <= uaMax; i += 5) {
      const ctx = this.canvases.ua.getContext("2d");
      this.drawHorizontalLine({
        ctx,
        value: i,
        alpha: i % 25 ? this.gridLineSecondaryAlpha : this.gridLinePrimaryAlpha,
      });
    }

    this.drawVerticalLines();
  }

  drawHorizontalLine({ ctx, value, alpha = 1 }) {
    const y =
      ctx.canvas === this.canvases.hr
        ? this.hrToPosition(value)
        : this.mmHgToPosition(value);
    ctx.lineWidth = this.gridLineWidth;
    ctx.strokeStyle = `rgba(255,0,0,${alpha})`;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(ctx.canvas.width, y);
    ctx.stroke();
  }

  drawVerticalLines() {
    let lineTime = this.startTime;
    if (!lineTime) return;
    const pixelsPerMs = this.pixelsPerMs;
    const minuteLinesOnly = pixelsPerMs * 10000 < 5;

    if (minuteLinesOnly) {
      lineTime = lineTime.startOf("second");
    } else {
      lineTime = lineTime.startOf("millisecond");
      const startSeconds = lineTime.second;
      if (startSeconds % 10) {
        lineTime = lineTime.plus({ seconds: 10 - startSeconds });
      }
    }

    while (lineTime < (this.endTime ?? DateTime.now())) {
      const x = this.timeToPosition({ time: lineTime, pixelsPerMs });
      _.each(this.canvases, (canvas) => {
        const ctx = canvas.getContext("2d");
        let alpha = this.gridLineSecondaryAlpha;
        if (!lineTime.second) {
          alpha = this.gridLinePrimaryAlpha;
          if (lineTime.minute % 3 === 0 && !minuteLinesOnly) {
            this.writeHrAxisLabels(x);
            this.writeUaAxisLabels(x);
          }
        }
        ctx.lineWidth = this.gridLineWidth;
        ctx.strokeStyle = `rgba(255,0,0,${alpha})`;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      });
      if (minuteLinesOnly) {
        lineTime = lineTime.plus({ minute: 1 });
      } else {
        lineTime = lineTime.plus({ seconds: 10 });
      }
    }
  }

  writeHrAxisLabels(x = 10, ctx = this.canvases.hr.getContext("2d")) {
    ctx = this.getAxisLabelContextValues(ctx);
    for (let i = 60; i < hrMax; i += 30) {
      const y = this.hrToPosition(i);
      ctx.strokeText(i, this.getAxisLabelAdjustedX(i, x), y);
      ctx.fillText(i, this.getAxisLabelAdjustedX(i, x), y);
    }
  }

  writeUaAxisLabels(x = 10, ctx = this.canvases.ua.getContext("2d")) {
    ctx = this.getAxisLabelContextValues(ctx);
    for (let i = uaMin + 25; i < uaMax; i += 25) {
      const y = this.mmHgToPosition(i);
      ctx.strokeText(i, this.getAxisLabelAdjustedX(i, x), y);
      ctx.fillText(i, this.getAxisLabelAdjustedX(i, x), y);
    }
  }

  getAxisLabelContextValues(ctx) {
    ctx.font = "10px sans-serif";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 5;
    ctx.fillStyle = "#000";
    return ctx;
  }

  getAxisLabelAdjustedX(i, x) {
    let adjustedX = x;
    const numLength = i.toString().length;
    if (numLength === 3) {
      adjustedX -= 8;
    } else if (numLength === 2) {
      adjustedX -= 6;
    }
    return adjustedX;
  }

  getColors() {
    this.colors = {};
    if (this.colorsRef)
      _.each(this.colorsRef.children, (el) => {
        this.colors[el.className] = getComputedStyle(el).color;
      });
  }
}

export default inject("store")(observer(ObWaveform));

function calcPixelsPerBpm(height = this.canvases.hr.height) {
  const min = hrMin;
  const max = hrMax;
  const bmpRange = max - min;
  const pixelsPerBpm = height / bmpRange;
  return pixelsPerBpm;
}

export function hrToPosition(bpm, height = this.canvases.hr.height) {
  const min = hrMin;
  const pixelsPerBpm = calcPixelsPerBpm(height);
  return height - (bpm - min) * pixelsPerBpm;
}

export function zoomStartTime({
  relativeX,
  e,
  startTime = this.props.summaryStore.startTime,
  minTime = this.props.summaryStore.minTime,
  endTime = this.props.summaryStore.endTime,
}) {
  return bracket({
    value: startTime.minus({ seconds: e.deltaY * relativeX }),
    min: DateTime.max(minTime, endTime.minus({ minutes: 10 })),
    max: endTime - 60 * 1000,
  });
}
export function zoomEndTime({
  relativeX,
  e,
  startTime = this.props.summaryStore.startTime,
  maxTime = this.props.summaryStore.maxTime,
  endTime = this.props.summaryStore.endTime,
}) {
  return bracket({
    value: endTime.plus({ seconds: e.deltaY * (1 - relativeX) }),
    min: startTime + 60 * 1000,
    max: maxTime,
  });
}
